import type { IncidentStatus } from "../shared/types";

const HOUR = 3600 * 1000;

export function formatFrp(mw: number | null | undefined): string {
  if (mw == null || !Number.isFinite(mw)) return "—";
  if (mw >= 100) return `${Math.round(mw)} MW`;
  if (mw >= 10) return `${mw.toFixed(0)} MW`;
  return `${mw.toFixed(1)} MW`;
}

export function formatDelta(mw: number, pct: number | null): string {
  const sign = mw > 0 ? "+" : "";
  const pctPart = pct == null ? "" : ` (${sign}${Math.round(pct)}%)`;
  return `${sign}${formatFrp(mw)}${pctPart}`;
}

export function formatAge(iso: string, nowMs: number): string {
  const ms = nowMs - Date.parse(iso);
  if (!Number.isFinite(ms)) return "—";
  if (ms < 0) return "just now";
  if (ms < 60_000) return "just now";
  if (ms < HOUR) return `${Math.round(ms / 60_000)} min ago`;
  if (ms < 24 * HOUR) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / (24 * HOUR))} d ago`;
}

/** Compact age without "ago" — nearby rows. */
export function formatAgeShort(iso: string, nowMs: number): string {
  const ms = nowMs - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 60_000) return "now";
  if (ms < HOUR) return `${Math.round(ms / 60_000)} min`;
  if (ms < 24 * HOUR) return `${Math.round(ms / HOUR)} h`;
  return `${Math.round(ms / (24 * HOUR))} d`;
}

/** Neighbor last-seen: "53 min" vs "2 d quiet" after a polar gap. */
export function formatNearbySeen(iso: string, nowMs: number, quietAfterMs: number): string {
  const age = nowMs - Date.parse(iso);
  const short = formatAgeShort(iso, nowMs);
  if (Number.isFinite(age) && age >= quietAfterMs) return `${short} quiet`;
  return short;
}

/** FIRMS latency: time from satellite observation to IGNIS storing the pixel. */
export function formatLag(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 60_000) return "<1 min";
  if (ms < HOUR) return `${Math.round(ms / 60_000)} min`;
  if (ms < 24 * HOUR) return `${(ms / HOUR).toFixed(1)} h`;
  return `${Math.round(ms / (24 * HOUR))} d`;
}

const CARDINALS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

export function formatCardinal(deg: number): string {
  const wrapped = ((deg % 360) + 360) % 360;
  const i = Math.round(wrapped / 22.5) % 16;
  return CARDINALS[i]!;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Visitor-local HH:MM. Storage and APIs stay UTC ISO. */
function localHm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Full local stamp for playhead, alerts, captions. */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${localHm(d)}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Look row: visitor-local clock. Date only when the look is not today locally. */
export function formatLookClock(iso: string, nowMs: number): string {
  const d = new Date(iso);
  const now = new Date(nowMs);
  const date = sameLocalDay(d, now) ? "" : `${d.getDate()} ${MONTHS[d.getMonth()]} `;
  return `${date}${localHm(d)}`;
}

/** Time only — the panel already says “4 h ago”. */
export function formatTimeLocal(iso: string): string {
  return localHm(new Date(iso));
}

export function formatClockShort(iso: string): string {
  return localHm(new Date(iso));
}

/**
 * Axis / hover stamp. Date when the point is not today locally, or when the
 * caller forces it (span crosses midnight).
 */
export function formatChartTick(iso: string, nowMs: number, forceDate = false): string {
  const d = new Date(iso);
  const now = new Date(nowMs);
  if (!forceDate && sameLocalDay(d, now)) return localHm(d);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${localHm(d)}`;
}

/** Day or day-range for an event-span FRP title — visitor-local. */
export function formatChartSpan(points: { t: number }[], nowMs: number): string {
  if (points.length === 0) return "";
  const a = new Date(points[0]!.t);
  const b = new Date(points[points.length - 1]!.t);
  if (sameLocalDay(a, b)) {
    if (sameLocalDay(a, new Date(nowMs))) return "today";
    return `${a.getDate()} ${MONTHS[a.getMonth()]}`;
  }
  return `${a.getDate()} ${MONTHS[a.getMonth()]} – ${b.getDate()} ${MONTHS[b.getMonth()]}`;
}

/** Chart heading from the event’s actual first–last overpass. */
export function frpChartTitle(points: { t: number }[], nowMs: number): string {
  const span = formatChartSpan(points, nowMs);
  return span ? `FRP (MW) · ${span}` : "FRP (MW)";
}

export function formatFirePercent(p: number | null | undefined): string {
  if (p == null || !Number.isFinite(p)) return "—";
  return `${Math.round(p * 100)}%`;
}

export function statusLabel(status: IncidentStatus): string {
  switch (status) {
    case "newly_detected":
      return "Newly detected";
    case "rapidly_intensifying":
      return "Rapidly intensifying";
    case "growing":
      return "Growing";
    case "holding":
      return "Holding";
    case "cooling":
      return "Cooling";
    default:
      return "Active";
  }
}

export function sourceLabel(source: string): string {
  switch (source) {
    case "VIIRS_SNPP":
      return "VIIRS S-NPP";
    case "VIIRS_NOAA20":
      return "VIIRS NOAA-20";
    case "VIIRS_NOAA21":
      return "VIIRS NOAA-21";
    case "MODIS":
      return "MODIS Aqua/Terra";
    case "MTG":
      return "MTG FCI";
    case "MSG":
      return "MSG SEVIRI";
    case "S3":
      return "Sentinel-3 SLSTR";
    case "CATALOG":
      return "Saved";
    default:
      return source;
  }
}

/** Log-row link text. S3 has no GIBS granule — the href is place/time context. */
export function observationLinkLabel(source: string): string {
  if (source === "S3") return "Sentinel-3 SLSTR · context";
  return sourceLabel(source);
}

export function updatedLabel(updatedAt: string | null, nowMs: number): string {
  if (!updatedAt) return "no ingest yet";
  const ms = nowMs - Date.parse(updatedAt);
  if (ms < 60_000) return "updated just now";
  if (ms < HOUR) return `updated ${Math.round(ms / 60_000)} min ago`;
  return `updated ${formatClock(updatedAt)}`;
}

export function detectionAgeOpacity(observedAt: string, playheadMs: number): number {
  const age = playheadMs - Date.parse(observedAt);
  if (age < 0) return 0;
  if (age < HOUR) return 1;
  if (age < 6 * HOUR) return 0.75;
  if (age < 24 * HOUR) return 0.55;
  return 0.4;
}
